import React, {FC} from 'react'
import {useIntl} from 'react-intl'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {KTIcon} from '@/_metronic/helpers'
import {Card} from '@/components/Card'
import { isAfter, subMonths } from 'date-fns'
import {SharedUser, SharedUserMatch} from '../../../core/_models'

interface Props {
  shared: SharedUser[]
}

const SharedUsersCard: FC<Props> = ({shared }) => {
  const intl = useIntl()
  const threshold = subMonths(new Date(), 3)
  return (
    <Card
      title={
        <div className="d-flex align-items-center">
          <span className="me-2">Linked accounts</span>
          <span className="badge bg-primary text-white">
            {shared.filter((s: SharedUser) => s.lastSeen && isAfter(s.lastSeen, threshold)).length} recents
          </span>
        </div>
      }
      id="shared-users"
      collapsable={true}
      collapse={true}
    >

      <i className="text-muted">
        Only the probable multi-accounts still active are displayed, only the matches by telephone number matched 100%.
      </i>
      <table className="table table-row-dashed table-row-gray-300 gy-7">
        <thead>
          <tr className="fw-bolder fs-6 text-gray-800">
            <th></th>
            <th>Customer</th>
            <th>Status</th>
            <th>Last seen</th>
            <th>Balance</th>
            <th>Match</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {shared.map((s: SharedUser, i: number) => (
            <tr key={`shared-${i}`}>
              <td>
                <span className={clsx('bullet bullet-vertical h-45px me-5', {
                  'bg-success': s.lastSeen && isAfter(s.lastSeen, threshold),
                  'bd-secondary': !s.lastSeen || !isAfter(s.lastSeen, threshold)
                })}></span>
              </td>
              <td>
                <div className='d-flex align-items-center'>
                  <Link
                    className='d-flex flex-column text-muted text-hover-primary'
                    target="_blank"
                    to={`/p2c/customers/${s.userId}/overview`}
                  >
                    <span className='text-gray-800 mb-1 fw-bold'>{s.username}</span>
                    <span>{s.email}</span>
                  </Link>
                </div>
              </td>
              <td>
                <span className={clsx('badge', {
                  'badge-info': s.status === 'PENDING_VALIDATION',
                  'badge-success': s.status === 'VALIDATED',
                  'badge-warning': s.status === 'DELETED',
                  'badge-danger': s.status === 'LOCKED',
                })}>
                  {intl.formatMessage({id: `CUSTOMER_STATUS_${s.status}`})}
                </span>
              </td>
              <td>
                {s.lastSeen && (
                  <span className=''>
                    {intl.formatDate(s.lastSeen, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: 'numeric',
                      second: 'numeric'
                    })}
                  </span>
                )}
              </td>
              <td>{s.balance}</td>
              <td>
                <div className='d-flex flex-column'>
                  {s.matchWith.map((match: SharedUserMatch, index: number) => (
                    <div key={`match-${index}`} className='d-flex text-gray-800 mb-1'>
                      <span className='col-4'>
                        {intl.formatDate(match.date, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: 'numeric',
                          second: 'numeric'
                        })}
                      </span>

                      <span className='fw-bold col-2'>
                        {match.type.charAt(0).toUpperCase() + match.type.slice(1)}:
                      </span>

                      <span className='col-6'>
                        {match.value}
                      </span>
                    </div>
                  ))}
                </div>
              </td>
              <td>
                <Link
                  className='text-muted text-hover-primary'
                  target="_blank"
                  to={`/p2c/customers/${s.userId}/overview`}
                >
                  <KTIcon iconName="exit-right-corner" className='fs-3' />
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  )
}

export default SharedUsersCard
